import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { ARTICLES, ARTICLE_IMAGES, IMAGES } from "@/data/site";
import Seo from "@/components/Seo";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-teal">Guide not found</h1>
        <Link to="/hub" className="mt-4 inline-block text-teal underline underline-offset-4">
          Back to the Planning Hub
        </Link>
      </div>
    );
  }

  const related = ARTICLES.filter((a) => a.category === article.category && a.slug !== slug).slice(0, 2);

  return (
    <div data-testid="article-page">
      <Seo title={article.title} description={article.excerpt} />
      <section className="bg-sand-light py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/hub"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-teal/70 hover:text-teal transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Planning Hub
          </Link>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-balance text-teal">
            {article.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/55">
            <span>{article.category}</span>
            <span>{article.level}</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {article.minutes} min
            </span>
          </div>
          {IMAGES[ARTICLE_IMAGES[article.slug]] && (
            <img
              src={IMAGES[ARTICLE_IMAGES[article.slug]]}
              alt={article.title}
              data-testid="article-hero-image"
              className="mt-8 w-full h-56 sm:h-72 object-cover border border-cloud"
              loading="lazy"
            />
          )}
        </div>
      </section>

      <article className="py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="font-serif text-xl sm:text-2xl text-teal leading-relaxed">
              {article.excerpt}
            </p>
          </FadeIn>
          <div className="mt-8 space-y-6">
            {article.body.map((p, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <p className="text-base sm:text-lg text-charcoal/85 leading-relaxed">{p}</p>
              </FadeIn>
            ))}
          </div>
          <FadeIn className="mt-12">
            <div className="bg-sand-light border border-cloud p-7">
              <p className="text-xs font-mono uppercase tracking-[0.18em] text-teal">Keep in mind</p>
              <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">
                This guide is general education, not advice on your specific property. Planning
                requirements vary by council, site and proposal — confirm with your local council
                or book a consultation before acting.
              </p>
            </div>
          </FadeIn>

          {related.length > 0 && (
            <div className="mt-14">
              <Eyebrow>Keep Reading</Eyebrow>
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/hub/${r.slug}`}
                    className="group bg-white border border-cloud p-6 hover:border-teal transition-colors"
                  >
                    <h3 className="font-serif text-lg text-teal leading-snug">{r.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-charcoal/60 group-hover:text-teal transition-colors">
                      Read next <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <FadeIn className="mt-14 text-center">
            <p className="font-serif text-2xl text-teal">Want eyes on your specific site?</p>
            <div className="mt-5 flex justify-center gap-3 flex-wrap">
              <Link
                to="/book"
                className="inline-flex items-center gap-2 bg-teal text-ivory font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
              >
                Book a Planner <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/toolkits"
                className="inline-flex items-center gap-2 border border-teal text-teal font-medium px-7 py-3.5 rounded-full hover:bg-teal hover:text-ivory transition-colors"
              >
                Browse Toolkits
              </Link>
            </div>
          </FadeIn>
        </div>
      </article>
    </div>
  );
};

export default ArticlePage;
